'use client';

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  CircularProgress,
  Button,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Pagination,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import { createTireSize, updateTireSize, getTireSizeById } from '../../../../stores/tireSizeStore';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { useVehicleTypeList } from '../../../../stores/vehicleTypeStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { formatUpperCase } from '../../../../utils/formatTitleCase';
import MasterCompanyAddEdit from '../../../master-data/company/MasterCompanyAddEdit';
import VehicleTypeAddEditV2 from '../../../master-data/vehicle-type/VehicleTypeAddEditV2';

const MasterSizeAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : '',
    code_internal: '',
    vehicle_type_ids: [],
    status: 'Active'
  });

  const [loading, setLoading] = useState(false);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([]);
  const [vtPage, setVtPage] = useState(1);

  const { data: vehicleTypesResp } = useVehicleTypeList(0, 1000, '', '');
  const vehicleTypes = useMemo(() => (Array.isArray(vehicleTypesResp) ? vehicleTypesResp : []), [vehicleTypesResp]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const vehicleTypePickerParams = usePickerPagination({
    fetchHook: useVehicleTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getTireSizeById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => {
            let parsedIds = [];
            let parsedNames = [];
            let vType = data.vehicle_type_ids || data.vehicle_type_id || data.vehicle_type;
            let vTypeName = data.join?.vehicle_type_name || data.vehicle_type_name;
            if (Array.isArray(vType)) {
              parsedIds = vType.map((v) => v.id ?? v.value ?? v);
              parsedNames = vType.map((v) => v.name ?? v.label ?? '-');
            } else if (typeof vType === 'string' && vType.includes(',')) {
              parsedIds = vType.split(',');
              parsedNames = typeof vTypeName === 'string' ? vTypeName.split(',') : [];
            } else if (vType) {
              parsedIds = [vType];
              parsedNames = [vTypeName || '-'];
            }

            const initialSelected = parsedIds.map((id, index) => ({
              id: String(id),
              name: (parsedNames[index] || String(id)).trim()
            }));
            setSelectedVehicleTypes(initialSelected);
            setVtPage(1);
            return {
              ...prev,
              company: data.company_id ?? data.company ?? prev.company,
              code_internal: data.code_internal ?? data.code ?? prev.code_internal,
              vehicle_type_ids: parsedIds,
              status: data.status ?? prev.status
            };
          });
        }
      } catch (err) {
        console.error('Failed to load tire size:', err);
        notify({ message: t('size.failedLoadData', 'Failed to load size data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company: Yup.string().required(t('general.validationCompanyRequired', 'Company is required')),
    code_internal: Yup.string().required(t('size.validationCodeRequired', 'Size Code is required')),
    vehicle_type_ids: Yup.array().min(1, t('size.validationApplicationRequired', 'Application is required')),
    status: Yup.string().required(t('general.validationStatusRequired', 'Status is required'))
  });

  const handleClose = () => {
    formik.resetForm();
    setSelectedVehicleTypes([]);
    setVtPage(1);
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company),
          code_internal: formatUpperCase(values.code_internal) ?? '',
          vehicle_type_ids: values.vehicle_type_ids.map(Number),
          status: values.status
        };

        if (id) {
          await updateTireSize(id, payload);
          notify({ message: t('size.updateSuccess', 'Size updated successfully'), severity: 'success' });
        } else {
          await createTireSize(payload);
          notify({ message: t('size.createSuccess', 'Size added successfully'), severity: 'success' });
        }

        formik.resetForm();
        setSelectedVehicleTypes([]);
        setVtPage(1);
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id ? t('general.edit', 'Edit') + ' ' + t('size.title', 'Tire Size') : t('general.add', 'Add') + ' ' + t('size.title', 'Tire Size')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('general.company', 'Company') + ' *'}
                  name="company"
                  allowAdd
                  AddComponent={MasterCompanyAddEdit}
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val)}
                  onBlur={() => formik.setFieldTouched('company', true)}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                  {...companyPickerParams}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('size.tireSize', 'Tire Size') + ' *'}
                  name="code_internal"
                  value={formik.values.code_internal}
                  onChange={(e) => {
                    formik.setFieldValue('code_internal', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  error={formik.touched.code_internal && Boolean(formik.errors.code_internal)}
                  helperText={formik.touched.code_internal && formik.errors.code_internal}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <PickerField
                      label={t('size.application', 'Application (Vehicle Type)') + ' *'}
                      allowAdd
                      AddComponent={VehicleTypeAddEditV2}
                      name="vehicle_type_ids"
                      value={formik.values.vehicle_type_ids}
                      onChange={(val) => {
                        formik.setFieldValue('vehicle_type_ids', val);
                        const newSelected = (val || []).map((id) => {
                          const existing = selectedVehicleTypes.find((s) => String(s.id) === String(id));
                          if (existing) return existing;
                          const opt =
                            vehicleTypePickerParams.options.find((o) => String(o.id ?? o.value) === String(id)) ||
                            vehicleTypePickerParams.listOptions.find((o) => String(o.id ?? o.value) === String(id));
                          return { id: String(id), name: opt ? (opt.name ?? opt.label ?? opt.value) : String(id) };
                        });
                        setSelectedVehicleTypes(newSelected);
                        if (Math.ceil(newSelected.length / 5) < vtPage) {
                          setVtPage(Math.max(1, Math.ceil(newSelected.length / 5)));
                        }
                      }}
                      onBlur={() => formik.setFieldTouched('vehicle_type_ids', true)}
                      error={formik.touched.vehicle_type_ids && Boolean(formik.errors.vehicle_type_ids)}
                      helperText={formik.touched.vehicle_type_ids && formik.errors.vehicle_type_ids}
                      multiSelect={true}
                      {...vehicleTypePickerParams}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, minHeight: '100%', bgcolor: '#fdfdfd' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected Applications ({selectedVehicleTypes.length})
                      </Typography>
                      {selectedVehicleTypes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No application selected
                        </Typography>
                      ) : (
                        <>
                          <List dense disablePadding>
                            {selectedVehicleTypes.slice((vtPage - 1) * 5, vtPage * 5).map((item) => (
                              <ListItem
                                key={item.id}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => {
                                      const nextIds = formik.values.vehicle_type_ids.filter((v) => String(v) !== String(item.id));
                                      formik.setFieldValue('vehicle_type_ids', nextIds);
                                      setSelectedVehicleTypes((prev) => {
                                        const next = prev.filter((p) => String(p.id) !== String(item.id));
                                        if (Math.ceil(next.length / 5) < vtPage) {
                                          setVtPage(Math.max(1, Math.ceil(next.length / 5)));
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" color="error" />
                                  </IconButton>
                                }
                                sx={{ borderBottom: '1px solid #f0f0f0' }}
                              >
                                <ListItemText primary={vehicleTypes.find((v) => String(v.id) === String(item.id))?.name || item.name} />
                              </ListItem>
                            ))}
                          </List>
                          {Math.ceil(selectedVehicleTypes.length / 5) > 1 && (
                            <Box display="flex" justifyContent="center" mt={1}>
                              <Pagination
                                size="small"
                                count={Math.ceil(selectedVehicleTypes.length / 5)}
                                page={vtPage}
                                onChange={(e, val) => setVtPage(val)}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  select
                  label={t('masterCompany.status', 'Status') + ' *'}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.status && Boolean(formik.errors.status)}
                  helperText={formik.touched.status && formik.errors.status}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterSizeAddEdit;
